import { isSlotWithin24Hrs, ParsedFilterType } from 'app/ProviderSearch/utils'
import {
  LOGIN_COUNT_KEY,
  PAGINATION_LIMIT,
  USER_PROVIDERS,
} from 'constants/api'
import {
  dateFormats,
  PractitionerTitle,
  practitionerTypes,
  providerSearchNumber,
  seFriendlyPractitionerTypes,
  sessionStorageKeys,
  USER_GEO_IP_LOCATION_KEY,
} from 'constants/common'
import { differenceInYears, format, parseISO, getHours } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import enUS from 'date-fns/locale/en-US'
import _omit from 'lodash/omit'
import _startCase from 'lodash/startCase'
import { NextRouter } from 'next/router'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'
import { MutableRefObject } from 'react'
import { Req } from 'use-http'
import { getGeocode, getLatLng } from 'use-places-autocomplete'
import {
  homePageRoutes,
  pageRoutes,
  thriveCarePageRoutes,
} from '../../constants/routing'
import { TWPERMISSIONS } from 'dataSource/auth/types'
import { datadogRum } from '@datadog/browser-rum'
import { LogEvent } from 'constants/logEvents'
import { SlotAvailabilityType } from 'app/Bookings/constants'
import { TimeOfDay } from 'constants/common'
export * from './getBirthDate'

/*
  formatUserQuery can be used to parse any user specified
  query not necessarily a router query
 */
export const formatUserQuery = (
  query: ParsedUrlQuery | UserQuery
): ParsedUrlQuery => {
  const formattedQuery = {}

  if (!query) {
    return formattedQuery
  }

  Object.keys(query).map((queryKey) => {
    if (queryKey.includes('[]')) {
      const formattedKey = queryKey.replace('[]', '')

      if (Array.isArray(query[queryKey])) {
        formattedQuery[formattedKey] = query[queryKey]
      } else {
        formattedQuery[formattedKey] = [query[queryKey]]
      }
    } else {
      formattedQuery[queryKey] = query[queryKey]
    }
  })

  return formattedQuery
}

interface UserQuery extends ParsedFilterType {
  counselingType?: string[]
  insurancePlan?: string
  insuranceId?: string
  limit?: string
  offset?: string
  practiceId?: string
  selfPay?: boolean
  latitude?: string
  longitude?: string
  state?: string
  patientId?: string
  location?: string
  appointmentMode?: string
  status?: string
}

export const modifyURLQuery = (
  router: NextRouter,
  userQuery: UserQuery | ParsedUrlQuery,
  removeExistingQuery = false,
  routerAction = 'push',
  newBasePath?: string
): void => {
  const { asPath } = router
  const parsedQuery = removeExistingQuery ? {} : getParsedQuery(router)
  const parsedUserQuery = formatUserQuery(userQuery)
  const basePath =
    newBasePath !== undefined ? newBasePath : asPath.split('?')[0]
  const parsedPathName = `${basePath}?${qs.stringify(
    {
      ...parsedQuery,
      ...parsedUserQuery,
    },
    { encode: false, arrayFormat: 'brackets', skipNulls: true }
  )}`

  // Next js router doesn't properly stringify nested objects
  // https://github.com/vercel/next.js/pull/2605
  // TODO check with new URL()
  router[routerAction](parsedPathName, undefined, {
    shallow: true,
  })
}

/**
 * This method is used to get the stringified query string with brackets as
 * array format
 * @param userQuery
 * @returns stringifiedQuery
 */

export const getStringifiedQuery = (
  userQuery: UserQuery | ParsedUrlQuery
): string => {
  return qs.stringify(userQuery, {
    encode: false,
    arrayFormat: 'brackets',
    skipNulls: true,
  })
}

// TODO move this to useAPI's response interceptor
export const hasError = (
  request: Req,
  response: CustomResponse,
  defaultMessage = 'Something went wrong! Please try again'
): void => {
  // Sample response for future reference
  // error{}->
  //  message: "Patient already exists."
  // -----  OR ----
  // error{}->
  //  message{} -> { description: "Patient does not have any relationship", patientDetails: {//patientDetails}}
  // -----  OR ----
  // error{}->
  //  address[]->
  //   constraints{}-> { isString: 'error message eg: line2 must be string'}

  if (request.error) {
    if (response?.error) {
      const { error } = response
      const normalizedError = new Error(defaultMessage)
      normalizedError.name = 'Response Error'

      if (error?.message) {
        if (typeof error.message === 'object') {
          normalizedError.message = error.message?.description || defaultMessage

          if (error.message.errorCode) {
            normalizedError.name = error.message.errorCode.toString()
          }
        } else {
          normalizedError.message = error.message
        }
      }

      if ((error?.message as MessageError)?.amdDescription) {
        normalizedError['errorFromAMD'] = true
      }

      const errorKeys = Object.keys(error)
      const singleError = error[errorKeys[0]]

      if (Array.isArray(singleError)) {
        const firstError = singleError[0]

        if (typeof firstError == 'object' && firstError.constraints) {
          const constraintKeys = Object.keys(firstError.constraints)
          normalizedError.message = firstError.constraints[constraintKeys[0]]
        } else if (typeof firstError === 'string') {
          normalizedError.message = firstError
        }
      } else if (typeof singleError === 'string') {
        normalizedError.message = singleError
      }

      const emailError =
        typeof error?.message === 'object' && 'errors' in error.message
          ? error.message.errors?.[0]?.reason
          : undefined

      if (emailError) {
        normalizedError.message = emailError
        throw normalizedError
      }

      throw normalizedError
    }

    throw request.error
  }
}

export const sortGeneratedSlots = (generatedSlots: DateSlot): DateSlot => {
  let sortedslots: DateSlot = {}

  for (const [key, value] of Object.entries(generatedSlots)) {
    const sortedValue = sortByDate(value, 'start', true)
    sortedslots = { ...sortedslots, [key]: sortedValue }
  }

  return sortedslots
}

export const sortByDate = <Type>(
  list: Type[],
  value: string,
  ascending = true
): Type[] => {
  return list?.sort((a, b) =>
    ascending
      ? new Date(a?.[value]).getTime() - new Date(b?.[value]).getTime()
      : new Date(b?.[value]).getTime() - new Date(a?.[value]).getTime()
  )
}

export const fetchDataAsync = (
  parsedQuery: qs.ParsedQs,
  route: string,
  backgroundRequest: Req,
  pagination: PaginationSchema,
  paginationLimit: number = PAGINATION_LIMIT
  // Type any is intentional here
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any>[] => {
  const { count, limit: initialOffet } = pagination
  let offset = initialOffet
  const promises = []

  while (offset < count) {
    const stringfiedQs = qs.stringify(
      {
        ...parsedQuery,
        offset,
        limit: paginationLimit,
      },
      { addQueryPrefix: true, skipNulls: true }
    )
    promises.push(backgroundRequest.get(`${route}${stringfiedQs}`))
    offset += paginationLimit
  }

  return promises
}

/**
 *
 * @param placeId placedId from google places api
 * @param usedForFeature place where the event is being called from.
 * @returns {firstRestult, latLong}
 */
export const getFirstLocationResultData = async (
  placeId: string,
  usedForFeature: string
): Promise<LocationResultType> => {
  datadogRum.addAction(LogEvent.GEOCODE_USED, {
    usedForFeature,
  })
  const results = await getGeocode({ placeId })
  const firstResult = results[0]
  const latLong = await getLatLng(firstResult)
  return { firstResult, latLong }
}

// TODO: Date-fns-tz recently (v1.3.4 and up, dec-2021) introduced formatInZonedTime which is what the getTimeZoneFormattedDate helper function does ..
// SO we can remove it and use formatInZonedTime
export const getTimeZoneFormattedDate = (
  date: Date | string | number,
  dateFormat: string,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const dt = !isNaN(Date.parse(date as string)) ? date : new Date()
  // IF the date variable is a date object then utcToZonedTime function handles removing of the local timezone

  return formatInTimeZone(dt, timeZone, dateFormat, { locale: enUS })
}

export const getCurrentZonnedTime = (timeZone: string): Date => {
  const utc = zonedTimeToUtc(
    new Date(),
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const zonedTime = utcToZonedTime(utc, timeZone)
  return zonedTime
}

export const isDateEqual = (date1: Date, date2: Date): boolean => {
  if (date1 && date2) {
    return (
      format(date1, dateFormats.YEAR_MONTH_DAY_FORMAT) ===
      format(date2, dateFormats.YEAR_MONTH_DAY_FORMAT)
    )
  }

  return false
}

export const isToday = (date: Date | number, timeZone: string): boolean => {
  if (!date) {
    return false
  }

  const today = format(
    getCurrentZonnedTime(timeZone),
    dateFormats.YEAR_MONTH_DAY_FORMAT
  )
  const dateToMatch = format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)

  return today === dateToMatch
}

export const getProviderProfileParams = (
  provider: Provider,
  practice?: Practice
): SlugBasedProfileParams | PracticeIdBasedProfileParams => {
  let providerProfileParams = {} as
    | SlugBasedProfileParams
    | PracticeIdBasedProfileParams
  const providerPractice = practice ?? provider.practice

  const { state } = providerPractice
  const formattedState = state.replace(/\s/g, '-').toLowerCase()

  if (!!providerPractice.state && !!providerPractice.providerSlugs) {
    let providerTitle = provider.title

    if (!providerTitle) {
      //TODO: Need to check if we can use getProviderTitle util function instead
      //of below code. Currently, using that is throwing a reference error
      providerTitle = provider?.filters?.find(
        (providerFilter) => providerFilter.type === 'provider_title'
      )?.value
    }

    const type = getPractitionerType(providerTitle)
    providerProfileParams = {
      type,
      state: formattedState,
      slug:
        providerPractice.providerSlugs[0].name ||
        providerPractice.providerSlugs[0],
    }
  } else {
    const { id: providerId, firstName, lastName } = provider
    const { id: practiceId, city } = providerPractice
    const providerName = firstName + lastName
    providerProfileParams = {
      state: formattedState,
      city: city.replace(/\s/g, '-').toLowerCase(),
      providerName: providerName.replace(/\s/g, '-').toLowerCase(),
      providerId,
      practiceId,
    }
  }

  return providerProfileParams
}

export const getPractitionerType = (title: string): string => {
  if (
    title === PractitionerTitle.NP ||
    title === PractitionerTitle.Psych_NP ||
    title === PractitionerTitle.PMHNP ||
    title === PractitionerTitle.Psych_PMHNP ||
    title === PractitionerTitle.DNP ||
    title === PractitionerTitle.PMHNP_FNP_DNP
  ) {
    return practitionerTypes.PROVIDER
  } else if (title === PractitionerTitle.DO || title === PractitionerTitle.MD) {
    return practitionerTypes.PSYCHIATRIST
  }

  return practitionerTypes.THERAPIST
}

export const checkPathIsProviderProfileURL = (asPath: string): boolean => {
  const basePath = asPath.split('?')[0]
  const pathSegregation = basePath.split('/').filter((path) => !!path)

  if (
    pathSegregation.length === 3 &&
    Object.values(practitionerTypes).includes(pathSegregation[0]) &&
    pathSegregation[1].length === 2
  ) {
    // This checks if the urlpath has 3 slugs
    // Then checking if the first slug matches our practitioner types
    // Then checking if the 2nd slug matches the length of a state abbreviation
    return true
  }

  return false
}

export const checkPathIsSEFriendlyURL = (asPath: string): boolean => {
  const basePath = asPath.split('?')[0]
  const pathSegregation = basePath.split('/').filter((path) => !!path)

  if (
    pathSegregation.length === 1 &&
    Object.values(seFriendlyPractitionerTypes).includes(pathSegregation[0])
  ) {
    // Checks if its the hub page
    return true
  } else if (
    pathSegregation.length === 3 &&
    Object.values(seFriendlyPractitionerTypes).includes(pathSegregation[0]) &&
    pathSegregation[1].length === 2
  ) {
    // Checks if its the listing page.
    return true
  }

  return false
}

export const calculateAge = (dob: string): number => {
  const date = parseISO(dob)
  const age = differenceInYears(new Date(), date)
  return age
}

// Formats to a us phone number format
export const formatPhoneNumber = (phoneNumberString: string): string => {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }

  return null
}

export const countProviderSlots = (
  providerAvailableSlots: DateSlot,
  skipSlotsWithin24Hours = true
): number => {
  const providerSlotsCount = Object.keys(providerAvailableSlots).reduce(
    (totalCount, slotDate) => {
      const validSlotList = providerAvailableSlots[slotDate].filter((slot) => {
        const isValidSlot = slot !== null

        if (skipSlotsWithin24Hours) {
          return isValidSlot && !isSlotWithin24Hrs(slot?.start)
        }

        return isValidSlot
      })

      return (totalCount = totalCount + validSlotList?.length)
    },
    0
  )
  return providerSlotsCount
}

export const countProviderSlotsShown = (
  providerAvailableSlots: DateSlot
): number => {
  const providerSlotsCount = Object.keys(providerAvailableSlots).reduce(
    (totalCount, slotDate) => {
      const notNulls = providerAvailableSlots[slotDate].filter((slot) => {
        return slot !== null
      })
      return (totalCount = totalCount + notNulls?.length)
    },
    0
  )
  return providerSlotsCount
}

// Checks if a given string is valid date
export const isDateValid = (date: string): boolean => !isNaN(Date.parse(date))

export const getFormattedPracticeAddress = (practice: Practice): Practice => {
  const formattedName = _startCase(practice.name?.toLowerCase())

  const formattedAddress = practice.address?.replace(/, US[A]?/, '') //Replaces US, USA from the address

  const formattedAddressLine1 = practice.addressLine1
    ?.split(', ')
    .map((addressPart) => _startCase(addressPart.toLowerCase()))
    .join(', ')

  return {
    ...practice,
    name: formattedName,
    address: formattedAddress,
    addressLine1: formattedAddressLine1,
  }
}

const getStreetAddressFromPractice = (practice) => {
  const { address, city, state, zipcode, country } = practice
  const addressComponents = address.split(',')
  const stateRe = new RegExp(state, 'g')
  const countryRe = new RegExp(country + '[A]?', 'g')
  const zipcodeRe = new RegExp(zipcode, 'g')

  const filteredAddressComponents = addressComponents
    .map((addressComponent) => addressComponent.trim())
    .filter(
      (addressComponent) =>
        !(
          city === addressComponent ||
          stateRe.test(addressComponent) ||
          zipcodeRe.test(addressComponent) ||
          countryRe.test(addressComponent)
        )
    )

  return filteredAddressComponents
}

export const getPracticeAddressFeatures = (
  practice: Practice
): {
  practiceName: string
  aptOrSuite: string
  streetAddress: string
  city: string
  state: string
  country: string
  zipcode: string
  fullAddress: string
  googlePlaceId: string | null
} => {
  const {
    name,
    addressLine1,
    city,
    state,
    zipcode,
    country,
    googlePlaceId,
  } = practice

  const formattedName = _startCase(name?.toLowerCase())
  const rawStreetAddress = getStreetAddressFromPractice(practice)
  const streetAddress = rawStreetAddress.join(', ')

  const formattedAptOrSuite = addressLine1
    ?.split(', ')
    .map((addressPart) => _startCase(addressPart.toLowerCase()))
    .join(', ')

  const formattedZipcode = zipcode?.slice(0, 5)

  const addressEnd = `${city}, ${state} ${formattedZipcode}`
  const fullAddress = formattedAptOrSuite
    ? `${streetAddress}, ${formattedAptOrSuite}, ${addressEnd}`
    : `${streetAddress}, ${addressEnd}`

  return {
    practiceName: formattedName,
    aptOrSuite: formattedAptOrSuite,
    streetAddress,
    city,
    state,
    country,
    zipcode: formattedZipcode,
    fullAddress,
    googlePlaceId,
  }
}

export const getUserLocation = (): string =>
  sessionStorage.getItem(USER_GEO_IP_LOCATION_KEY)

export const getConcatenatedPatientNames = (
  patients: PatientDetails[]
): string => {
  if (patients?.length === 1) {
    return getPatientName(patients[0])
  }

  const patientNamesArr = patients
    ?.map((patient) => getPatientName(patient))
    .sort((name1, name2) => name1.localeCompare(name2))

  const patientNamesLength = patientNamesArr?.length - 1
  const restOfPatients = patientNamesArr?.slice(0, patientNamesLength)
  return `${restOfPatients?.join(', ')} and ${
    patientNamesArr?.[patientNamesLength]
  }`
}

const getPatientName = (patient: PatientDetails) => {
  return `${patient.firstName} ${patient.lastName}`
}

// Strips pathname from query params and parsed Query in form of object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getParsedQuery = (router: NextRouter): Record<string, any> => {
  const { asPath } = router
  const queryString = asPath.split('?')[1]
  const parsedQueryObj = qs.parse(queryString)
  return parsedQueryObj
}

export const clearNonPersistingDataInSession = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
}

export const removeKeywordFromStoredSearchQuery = (): void => {
  const storedProviderSearchQuery = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PROVIDER_SEARCH_QUERY)
  )
  sessionStorage.setItem(
    sessionStorageKeys.PROVIDER_SEARCH_QUERY,
    JSON.stringify(_omit(storedProviderSearchQuery, ['partnershipCode']))
  )
}

export const removeVerifiedInsuranceInfo = (): void => {
  sessionStorage.removeItem(sessionStorageKeys.VERIFIED_INSURANCE_AUDIT_ID)
  sessionStorage.removeItem(sessionStorageKeys.VERIFIED_INSURANCE_CARRIER_ID)
}

export const scrollToDiv = (
  containerRef: MutableRefObject<HTMLDivElement>,
  scrollProps?: Record<string, unknown>,
  scrollMethod = 'scrollIntoView'
): void => {
  containerRef?.current?.[scrollMethod]({ behavior: 'smooth', ...scrollProps })
}

export enum ErrorMessage {
  MAX = 'Max',
  NOT_VALID = 'Not Valid',
  REQUIRED = 'Required',
}

export function getErrorMessage(
  type: string,
  value: string,
  isSupportOrThirdPartyUser = false
): string {
  switch (type) {
    case ErrorMessage.REQUIRED:
      if (isSupportOrThirdPartyUser) {
        return `Please enter the ${value}${
          value === 'relationship' ? ' to the client' : ''
        }`
      } else {
        return `Oops, this is a mandatory field! Please enter your ${value}`
      }

    case ErrorMessage.NOT_VALID:
      return `Something's not right, please enter a valid ${value}`

    case ErrorMessage.MAX:
      return `Please enter a valid ${value}`
  }
}

export const isFirstTimeUser = (user: Partial<User>): boolean => {
  const loginCount = user?.[LOGIN_COUNT_KEY] || 0
  const userProviders = user?.[USER_PROVIDERS] || []
  return loginCount === 1 && userProviders.length === 1
}

export const getGenericErrorMessage = (): string => {
  const { formattedPhoneNumber } = providerSearchNumber
  return `Sorry, an error occurred while loading this page. Please refresh the page or call ${formattedPhoneNumber} to speak with a representative`
}

export const removeThriveworksFromString = (string: string): string => {
  return string.replace(/THRIVEWORKS/gi, '')
}

export const doesStringHaveValue = (val: string | undefined): boolean =>
  typeof val === 'string' && val.length > 0

export const consentForMinorText = (childName: string): string =>
  `Parent / legal guardian of ${childName}`

export const getHasFutureSlots = (
  nextAvailabilityForProviders: NextAvailabilityMap
): boolean => {
  return Object.keys(nextAvailabilityForProviders).some(
    (key) => nextAvailabilityForProviders[key] !== null
  )
}

export const splitDateAndTime = (
  formattedDate: Date
): { date: string; time: string } => {
  const validDate = formattedDate instanceof Date ? formattedDate : new Date()
  const dateAndTime = new Date(validDate).toISOString().split('T')

  return {
    date: dateAndTime[0],
    time: dateAndTime[1],
  }
}

export const choosePermissionBasedUserDestination = (
  permissions: TWPERMISSIONS[],
  redirectUrl?: string
): string => {
  const routeBasedOnPermission = permissions.find((p) => homePageRoutes[p])
    ? homePageRoutes[permissions.find((p) => homePageRoutes[p])]
    : pageRoutes.DASHBOARD_USER_HOME

  return (
    redirectUrl ||
    (routeBasedOnPermission ===
    thriveCarePageRoutes.THRIVECARE_CLINICIAN_SCHEDULE
      ? thriveCarePageRoutes.THRIVECARE_CLINICIAN_HOME_V1
      : routeBasedOnPermission)
  )
}

export function sortedStringify(obj: Record<string, unknown>): string {
  if (typeof obj !== 'object' || obj === null) {
    return JSON.stringify(obj)
  }

  const keys = Object.keys(obj).sort()
  const sortedObj = keys.reduce<Record<string, unknown>>((result, key) => {
    const val = obj[key]

    if (isRecord(val)) {
      result[key] = sortedStringify(val)
    } else {
      result[key] = obj[key]
    }

    return result
  }, {})

  return JSON.stringify(sortedObj)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function hashSfAddress(address: LeadAddress): string {
  // Convert the address object into a sorted string
  const addressString = sortedStringify({ ...address })

  // Encode the string into base64
  const hash = btoa(addressString)

  return hash
}

export function getNumbersOnlyFromString(
  numberString?: string
): string | undefined {
  return numberString ? numberString.replace(/[^0-9]/g, '') : undefined
}

export function convertHtmlToString(htmlString: string): string {
  // Use a regular expression to remove HTML tags
  return htmlString.replace(/<[^>]+>/g, '').trim()
}

export const filterDateSlotsByAppointmentMode = (
  dateSlots: DateSlot,
  appointmentMode: SlotAvailabilityType
): DateSlot => {
  const dates = Object.keys(dateSlots)
  const filteredSlots = {}

  dates.map((date) => {
    const slots = dateSlots[date].map((slot) => {
      return appointmentMode === undefined
        ? slot
        : slot.availabilityType.includes(appointmentMode) && slot
    })

    filteredSlots[date] = slots
  })

  return filteredSlots
}

export const getSlotWithTimeOfDayAdded = (
  slot: ProviderSlot,
  timeOfDay: TimeOfDay[],
  timeZone: string
): ProviderSlot | null => {
  const slotHour = getHours(utcToZonedTime(slot.start, timeZone))
  let slotInterval: TimeOfDay

  if (slotHour < 9) {
    // before 9am
    slotInterval = TimeOfDay.EARLY_MORNING
  } else if (slotHour >= 9 && slotHour < 12) {
    // from 9am to 12pm
    slotInterval = TimeOfDay.MORNING
  } else if (slotHour >= 12 && slotHour < 17) {
    // from 12pm to 5pm
    slotInterval = TimeOfDay.AFTERNOON
  } else {
    // after 5pm
    slotInterval = TimeOfDay.EVENING
  }

  if (timeOfDay.includes(slotInterval)) {
    return {
      ...slot,
      timeOfDay: slotInterval,
    }
  }

  return null
}

export const isValidEmail = (email: string): boolean => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
}
